import {
  useDispatchGetPersonalityReport,
  useIsLoading,
  useSelectPersonalityIntroAnalysis,
  useSelectPersonalityReportGraphs,
  useSelectPersonalityType,
} from 'affiny-core/hooks';
import { EAsyncEventTypes } from 'affiny-core/modules/asynchronousState/types';
import { Container } from 'affiny-desktop/src/components/desktop/Container';
import { Loader } from 'affiny-ui/src';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { animateScroll } from 'react-scroll';
import { Tabs } from '../Tabs';
import { PersonalityReportLayerPersonalityChapter } from './PersonalityReportLayerPersonalityChapter';
import { PersonalityReportLayerOnboardingCard } from './PersonalityReportLayerOnboardingCard';
import { PersonalityReportLayerValuesChapter } from './PersonalityReportLayerValuesChapter';
import { PersonalityReportLayerVisionChapter } from './PersonalityReportLayerVisionChapter';

export const PersonalityReportLayer = () => {
  const { t } = useTranslation();

  useDispatchGetPersonalityReport();

  const isLoadingPersonalityReport = useIsLoading(
    EAsyncEventTypes.GET_PERSONALITY_REPORT
  );

  const personalityType = useSelectPersonalityType();
  const personalityIntroAnalysis = useSelectPersonalityIntroAnalysis();
  const graphs = useSelectPersonalityReportGraphs();

  const sections = useMemo(
    () => [
      {
        label: 'value',
        title: t('personalityReport.tabs.value'),
      },
      {
        label: 'vision',
        title: t('personalityReport.tabs.vision'),
      },
      {
        label: 'personality',
        title: t('personalityReport.tabs.personality'),
      },
    ],
    [t]
  );

  const [section, setSection] = useState(sections[0].label);

  const goToNextSection = (label: string) => {
    animateScroll.scrollTo(550);
    setSection(label);
  };

  const renderSection = () => {
    switch (section) {
      case 'vision':
        return (
          personalityType && (
            <PersonalityReportLayerVisionChapter
              graphs={graphs.vision}
              personalityType={personalityType}
              setSection={goToNextSection}
            />
          )
        );
      case 'personality':
        return (
          <PersonalityReportLayerPersonalityChapter
            graphs={graphs.personality}
            personalityIntroAnalysis={personalityIntroAnalysis}
            setSection={goToNextSection}
          />
        );
      default:
        return (
          personalityType && (
            <PersonalityReportLayerValuesChapter
              graphs={graphs.values}
              setSection={goToNextSection}
            />
          )
        );
    }
  };

  return (
    <Container>
      <Loader isLoading={isLoadingPersonalityReport}>
        <PersonalityReportLayerOnboardingCard
          personalityType={personalityType}
          introOnboardingCard={
            graphs.vision[0] ? graphs.vision[0].analysis[0] : ''
          }
        />
        <Tabs
          sections={sections}
          section={section}
          setSection={goToNextSection}
        />
        {renderSection()}
      </Loader>
    </Container>
  );
};
